import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { Archive, Flame, ShoppingBag, Package, TrendingUp, Store } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DropActionsModal from '@/components/drops/DropActionsModal';
import UserBalanceWidget from '@/components/balance/UserBalanceWidget';

interface Drop {
  id: string;
  rarity: string;
  serial_number: string;
  media_url: string;
  user_action: string | null;
  unit_value_cents: number;
  claimed_at: string;
  drop_collections: {
    name: string;
    icon_url: string;
  };
}

const DropsInventory: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [drops, setDrops] = useState<Drop[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'stashed' | 'listed'>('all');
  const [selectedDrop, setSelectedDrop] = useState<Drop | null>(null);

  useEffect(() => {
    if (user?.id) {
      loadDrops();
    }
  }, [user?.id, filter]);

  const loadDrops = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('drops')
        .select(`
          *,
          drop_collections!drops_collection_id_fkey (
            name,
            icon_url
          )
        `)
        .eq('user_id', user!.id)
        .eq('claimed', true)
        .order('claimed_at', { ascending: false });

      if (filter === 'stashed') {
        query = query.eq('user_action', 'stashed');
      } else if (filter === 'listed') {
        query = query.eq('user_action', 'listed');
      } else {
        // All: exclude burned drops
        query = query.neq('user_action', 'burned');
      }

      const { data, error } = await query;

      if (error) throw error;
      setDrops(data || []);
    } catch (error: any) {
      console.error('Error loading drops:', error);
      toast({
        title: 'Failed to load drops',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const getRarityColor = (rarity: string) => {
    const colors = {
      common: 'bg-emerald-500/20 text-emerald-600 border-emerald-500/30',
      uncommon: 'bg-blue-500/20 text-blue-600 border-blue-500/30',
      rare: 'bg-purple-500/20 text-purple-600 border-purple-500/30',
      legendary: 'bg-orange-500/20 text-orange-600 border-orange-500/30',
    };
    return colors[rarity as keyof typeof colors] || colors.common;
  };

  const getActionIcon = (action: string | null) => {
    switch (action) {
      case 'stashed':
        return <Archive className="w-4 h-4" />;
      case 'listed':
        return <ShoppingBag className="w-4 h-4" />;
      case 'burned':
        return <Flame className="w-4 h-4" />;
      default:
        return <Package className="w-4 h-4" />;
    }
  };

  const stats = {
    total: drops.length,
    stashed: drops.filter(d => d.user_action === 'stashed').length,
    listed: drops.filter(d => d.user_action === 'listed').length,
    totalValue: drops.reduce((sum, d) => sum + (d.unit_value_cents || 0), 0),
  };

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      {/* Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-4xl font-bold mb-2">My Drops</h1>
          <p className="text-muted-foreground">
            Manage your collected drops and earnings
          </p>
        </div>
        <Button
          onClick={() => navigate('/drops')}
          variant="outline"
          className="gap-2"
        >
          <Store className="w-4 h-4" />
          Marketplace
        </Button>
      </div>

      {/* Balance Widget */}
      <div className="mb-8 max-w-md">
        <UserBalanceWidget />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Package className="w-6 h-6 text-primary" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats.total}</div>
                <div className="text-sm text-muted-foreground">Total Drops</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gray-500/10 flex items-center justify-center">
                <Archive className="w-6 h-6 text-gray-500" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats.stashed}</div>
                <div className="text-sm text-muted-foreground">Stashed</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                <ShoppingBag className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <div className="text-2xl font-bold">{stats.listed}</div>
                <div className="text-sm text-muted-foreground">Listed</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <div className="text-2xl font-bold">${(stats.totalValue / 100).toFixed(2)}</div>
                <div className="text-sm text-muted-foreground">Potential Value</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Tabs value={filter} onValueChange={(v) => setFilter(v as any)} className="mb-6">
        <TabsList>
          <TabsTrigger value="all">All ({stats.total})</TabsTrigger>
          <TabsTrigger value="stashed">Stashed ({stats.stashed})</TabsTrigger>
          <TabsTrigger value="listed">Listed ({stats.listed})</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Drops Grid */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="aspect-[2/3] bg-muted animate-pulse rounded-xl" />
          ))}
        </div>
      ) : drops.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Package className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-xl font-bold mb-2">No drops yet</h3>
            <p className="text-muted-foreground mb-6">
              {filter === 'all' 
                ? 'Claim drops from the artstream to start your collection'
                : `No ${filter} drops found`}
            </p>
            <Button onClick={() => window.location.href = '/artstream'}>
              Browse Drops
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {drops.map((drop) => (
            <Card
              key={drop.id}
              className="group cursor-pointer hover:shadow-xl transition-all duration-300 overflow-hidden"
              onClick={() => setSelectedDrop(drop)}
            >
              <div className="aspect-[2/3] relative overflow-hidden">
                {/* Drop Image */}
                <img
                  src={drop.media_url}
                  alt={drop.rarity}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />

                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                {/* Info Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/90 to-transparent">
                  <Badge className={`text-xs mb-2 ${getRarityColor(drop.rarity)}`}>
                    {drop.rarity}
                  </Badge>
                  <div className="text-white text-xs font-bold">#{drop.serial_number}</div>
                  <div className="text-white/60 text-xs truncate">{drop.drop_collections?.name}</div>
                </div>

                {/* Action Badge */}
                {drop.user_action && (
                  <div className="absolute top-2 right-2">
                    <Badge variant="secondary" className="flex items-center gap-1">
                      {getActionIcon(drop.user_action)}
                      <span className="text-xs capitalize">{drop.user_action}</span>
                    </Badge>
                  </div>
                )}

                {/* Value Badge */}
                <div className="absolute top-2 left-2">
                  <Badge className="bg-green-600 text-white">
                    ${(drop.unit_value_cents / 100).toFixed(2)}
                  </Badge>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Actions Modal */}
      {selectedDrop && (
        <DropActionsModal
          drop={selectedDrop}
          onClose={() => {
            setSelectedDrop(null);
            loadDrops(); // Refresh after action
          }}
        />
      )}
    </div>
  );
};

export default DropsInventory;
